import { InputBase } from '@mui/material';
import useControlled from '@mui/utils/useControlled';
import classNames from 'classnames';
import { InputHTMLAttributes, ReactNode, forwardRef, useImperativeHandle, useRef } from 'react';
import css from './LibraryNameInput.module.scss';

export type LibraryNameInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> & {
  onValueChange?: (value: string) => void;
  /** Controlled value. When provided, the parent owns the value via onValueChange. */
  value?: string;
  /** Rendered inside the pill at the trailing edge (e.g. an "advanced settings" cog). */
  endAdornment?: ReactNode;
};

export interface LibraryNameInputRef {
  getValue: () => string;
  focus: () => void;
  getInputRef: () => HTMLInputElement | null;
}

export const LibraryNameInput = forwardRef<LibraryNameInputRef, LibraryNameInputProps>(
  ({ className, onValueChange, value: valueProp, endAdornment, disabled, ...props }, ref) => {
    // Controlled when `value` is passed; otherwise self-managed (read via ref).
    const [value, setValue] = useControlled({
      controlled: valueProp,
      default: '',
      name: 'LibraryNameInput',
    });
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      getValue: () => value,
      focus: () => inputRef.current?.focus(),
      getInputRef: () => inputRef.current,
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      onValueChange?.(newValue);
    };

    return (
      <InputBase
        className={classNames(css.root, className)}
        type="text"
        value={value}
        onChange={handleChange}
        inputRef={inputRef}
        endAdornment={endAdornment}
        disabled={disabled}
        fullWidth
        // Arbitrary input attrs (id, placeholder, required, aria-*) go on the <input>.
        inputProps={{ ...props, className: css.input }}
      />
    );
  },
);
